import workspace from "../../../../../canvas/core/workspace";
import {CABINET_LINE_ROLE} from "../../../../../canvas/core/shapes/shapeParts/shapeCabinet";
import {isDebugFlagEnabled} from "./debugFlags";

const DEBUG_ALL_CABINET_LINES_CONFIG_KEYS = ['sqftDebugCabinetLines'];
const DEFAULT_LINE_HEIGHT_INCHES = 96;
const DEBUG_LINE_ROLE_SETTINGS = {
  [CABINET_LINE_ROLE.BACK]: {
    configKeys: ['sqftDebugBackLines', 'debugCabinetBackLines'],
    getLinesMethod: 'getBackLinesOnWorkspace',
    color: 0xff00ff,
  },
  [CABINET_LINE_ROLE.SIDE]: {
    configKeys: ['sqftDebugSideLines', 'debugCabinetSideLines'],
    getLinesMethod: 'getSideLinesOnWorkspace',
    color: 0x00d7ff,
  },
};

function getEnabledLineRoles() {
  const isAllLinesEnabled = isDebugFlagEnabled(DEBUG_ALL_CABINET_LINES_CONFIG_KEYS);

  return Object.keys(DEBUG_LINE_ROLE_SETTINGS)
    .filter(role => (
      isAllLinesEnabled
      || isDebugFlagEnabled(DEBUG_LINE_ROLE_SETTINGS[role].configKeys)
    ));
}

function getDebugLineHeightInches() {
  const config = document.kvConfig || {};
  const configuredHeight = Number(config.sqftDebugCabinetLineHeight || config.sqftDebugBackLineHeight);

  return Number.isFinite(configuredHeight) && configuredHeight > 0
    ? configuredHeight
    : DEFAULT_LINE_HEIGHT_INCHES;
}

function getCabinetLines(enabledRoles) {
  return workspace.getShapes(true)
    .filter(shape => (
      shape
      && !shape.isHidden
      && shape.shapeCabinet
    ))
    .reduce((lines, shape) => {
      enabledRoles.forEach(role => {
        const roleSettings = DEBUG_LINE_ROLE_SETTINGS[role];
        const getLines = shape.shapeCabinet[roleSettings.getLinesMethod];

        if (typeof getLines !== 'function') {
          return;
        }

        getLines.call(shape.shapeCabinet).forEach((line, lineIndex) => {
          if (!line || !line.from || !line.to) {
            return;
          }

          lines.push({
            role,
            shapeId: shape.id,
            lineIndex,
            from: line.from,
            to: line.to,
          });
        });
      });

      return lines;
    }, []);
}

function formatCoord(value) {
  return Number(value || 0).toFixed(3);
}

function getLinesKey(lines, modelScale) {
  return [
    formatCoord(modelScale),
    ...lines.map(line => [
      line.shapeId,
      line.role,
      line.lineIndex,
      formatCoord(line.from.x),
      formatCoord(line.from.y),
      formatCoord(line.to.x),
      formatCoord(line.to.y),
    ].join(':')),
  ].join('|');
}

function addLineRectPoints(points, line, modelScale, lineHeight) {
  const from = new window.THREE.Vector3(line.from.x * modelScale, 0, line.from.y * modelScale);
  const to = new window.THREE.Vector3(line.to.x * modelScale, 0, line.to.y * modelScale);
  const fromTop = from.clone();
  const toTop = to.clone();

  fromTop.y = lineHeight;
  toTop.y = lineHeight;

  points.push(
    from, to,
    fromTop, toTop,
    from, fromTop,
    to, toTop
  );
}

export default class CabinetLineDebugOverlay {
  constructor(scene, getModelScale) {
    this.scene = scene;
    this.getModelScale = getModelScale;
    this.group = null;
    this.materials = Object.keys(DEBUG_LINE_ROLE_SETTINGS).reduce((materials, role) => {
      materials[role] = new window.THREE.LineBasicMaterial({
        color: DEBUG_LINE_ROLE_SETTINGS[role].color,
        transparent: true,
        opacity: 0.95,
        depthTest: false,
        depthWrite: false,
      });

      return materials;
    }, {});
    this.lastLinesKey = null;
  }

  update() {
    const enabledRoles = getEnabledLineRoles();

    if (!enabledRoles.length) {
      this.clear();
      return;
    }

    const modelScale = this.getModelScale();
    const cabinetLines = getCabinetLines(enabledRoles);
    const linesKey = getLinesKey(cabinetLines, modelScale);

    if (linesKey === this.lastLinesKey) {
      return;
    }

    this.clear();
    this.lastLinesKey = linesKey;

    if (!cabinetLines.length) {
      return;
    }

    const lineHeight = getDebugLineHeightInches() * modelScale;
    const pointsByRole = cabinetLines.reduce((pointsMap, line) => {
      if (!pointsMap[line.role]) {
        pointsMap[line.role] = [];
      }

      addLineRectPoints(pointsMap[line.role], line, modelScale, lineHeight);

      return pointsMap;
    }, {});

    this.group = new window.THREE.Group();
    this.group.name = 'CabinetLineDebugOverlay';
    Object.keys(pointsByRole).forEach(role => {
      const geometry = new window.THREE.BufferGeometry().setFromPoints(pointsByRole[role]);
      const lineSegments = new window.THREE.LineSegments(geometry, this.materials[role]);
      lineSegments.renderOrder = 10000;
      lineSegments.userData.isCabinetLineDebugOverlay = true;
      lineSegments.userData.cabinetLineRole = role;

      this.group.add(lineSegments);
    });
    this.scene.add(this.group);
  }

  clear() {
    this.lastLinesKey = null;

    if (!this.group) {
      return;
    }

    this.group.traverse(child => {
      if (child.geometry) {
        child.geometry.dispose();
      }
    });
    this.scene.remove(this.group);
    this.group = null;
  }

  dispose() {
    this.clear();
    Object.values(this.materials).forEach(material => material.dispose());
  }
}
